import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppFrame } from "@/components/AppFrame";
import { cachedGet, getFresh, todayStr, nowHM, fmtDayMonth } from "@/lib/api";

const BAR_H = 96;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dowOf = (date) => DOW[new Date(date + "T12:00:00").getDay()];

const pctText = (v) => (v === null || v === undefined ? "–" : `${v}%`);

const tierOf = (v) => {
  if (v === null || v === undefined) return "none";
  if (v >= 80) return "good";
  if (v >= 50) return "ok";
  return "low";
};

const pctColor = (v) =>
  ({ none: "text-slate-600", good: "text-emerald-300", ok: "text-amber-300", low: "text-red-300" }[tierOf(v)]);

const barTone = (v, isToday) => {
  if (isToday)
    return "bg-gradient-to-t from-sky-500 to-cyan-300 shadow-[0_0_14px_rgba(56,189,248,0.55)]";
  const fills = {
    good: "bg-gradient-to-t from-emerald-600 to-emerald-400",
    ok: "bg-gradient-to-t from-amber-600 to-amber-400",
    low: "bg-gradient-to-t from-red-700 to-red-400",
  };
  return `${fills[tierOf(v)]} opacity-70`;
};

const Chart = ({ daily, today, onPick }) => {
  const scroller = useRef(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [daily]);

  return (
    <div ref={scroller} className="overflow-x-auto pb-1" data-testid="insights-chart-scroller">
      <div className="flex items-end gap-1.5 pr-1">
        {daily.map((d) => {
          const isToday = d.date === today;
          const has = d.pct !== null;
          return (
            <button
              key={d.date}
              onClick={() => onPick(d.date)}
              className="flex w-[40px] shrink-0 flex-col items-center transition-opacity duration-150 active:opacity-60"
              data-testid={`bar-${d.date}`}
            >
              <span
                className={`text-[13px] font-bold ${isToday ? "text-cyan-200" : pctColor(d.pct)}`}
                data-testid={`bar-pct-${d.date}`}
              >
                {pctText(d.pct)}
              </span>
              <span className="mb-1 text-[12px] leading-tight text-slate-400" data-testid={`bar-frac-${d.date}`}>
                {has ? `${d.taken}/${d.expected}` : ""}
              </span>
              <div className="flex h-[96px] w-full items-end justify-center">
                {has ? (
                  <div
                    style={{ height: `${Math.max(Math.round((d.pct / 100) * BAR_H), 3)}px` }}
                    className={`w-[20px] rounded-t-md transition-all duration-300 ${barTone(d.pct, isToday)}`}
                  />
                ) : (
                  <div className="h-[3px] w-[20px] rounded-full bg-slate-800" />
                )}
              </div>
              <span
                className={`mt-1.5 whitespace-nowrap text-[10px] leading-tight ${
                  isToday ? "font-semibold text-cyan-200" : "text-slate-400"
                }`}
              >
                {isToday ? "Today" : fmtDayMonth(d.date)}
              </span>
              <span
                className={`whitespace-nowrap text-[10px] leading-tight ${
                  isToday ? "text-cyan-300/80" : "text-slate-400"
                }`}
              >
                {dowOf(d.date)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function Insights() {
  const today = todayStr();
  const navigate = useNavigate();
  const [data, setData] = useState(() => cachedGet("/insights", { end: today, days: 60 }) || null);

  useEffect(() => {
    getFresh("/insights", { end: today, days: 60, now: nowHM() }, { end: today, days: 60 })
      .then(setData)
      .catch(() => {
        toast.error("Could not load insights");
        setData((prev) => prev || { daily: [], medicines: [], failed: true });
      });
  }, [today]);

  if (!data)
    return (
      <AppFrame title="Insights">
        <div className="space-y-3 pt-2">
          <div className="h-44 animate-pulse rounded-2xl bg-slate-900/60" />
          <div className="h-40 animate-pulse rounded-2xl bg-slate-900/60" />
        </div>
      </AppFrame>
    );

  if (data.medicines.length === 0)
    return (
      <AppFrame title="Insights">
        <p
          className="py-14 text-center text-[15px] text-slate-400"
          data-testid={data.failed ? "insights-error" : "empty-insights"}
        >
          {data.failed ? "Could not load insights. Reopen this tab to retry." : "Add a medicine to see insights."}
        </p>
      </AppFrame>
    );

  return (
    <AppFrame title="Insights">
      <div className="space-y-3 pb-4" data-testid="insights-page">
        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3">
          <h2 className="mb-2 text-[15px] font-semibold text-slate-200">Daily Compliance</h2>
          <Chart daily={data.daily} today={today} onPick={(d) => navigate(`/day?date=${d}`)} />
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3" data-testid="insights-table">
          <h2 className="mb-2 text-[15px] font-semibold text-slate-200">By Medicine</h2>
          <table className="w-full table-fixed">
            <thead>
              <tr>
                <th className="pb-1.5 text-left text-[12px] font-medium text-slate-500" />
                {data.week_labels.map((w, i) => (
                  <th
                    key={w}
                    className={`w-[48px] pb-1.5 pr-2 text-right text-[12px] text-slate-500 ${
                      i === 0 ? "font-bold text-slate-400" : "font-medium"
                    }`}
                  >
                    {w}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-sky-500/20 bg-sky-500/[0.08]" data-testid="insight-row-overall">
                <td className="truncate py-2 pl-1 pr-2 text-[14px] font-semibold text-sky-200">Overall</td>
                {data.overall.map((w, i) => (
                  <td
                    key={i}
                    className={`py-2 pr-2 text-right text-[14px] ${pctColor(w.pct)} ${i === 0 ? "font-bold" : ""}`}
                    data-testid={`insight-overall-w${i}`}
                  >
                    {pctText(w.pct)}
                  </td>
                ))}
              </tr>
              {data.medicines.map((m) => (
                <tr
                  key={m.med_id}
                  data-testid={`insight-row-${m.med_id}`}
                  onClick={() => navigate(`/compliance/${m.med_id}`)}
                  className="cursor-pointer border-t border-slate-800/50 transition-colors duration-200 active:bg-slate-800/40"
                >
                  <td className="truncate py-2 pl-1 pr-2 text-[14px] text-slate-200">{m.name}</td>
                  {m.weeks.map((w, i) => (
                    <td
                      key={i}
                      className={`py-2 pr-2 text-right text-[14px] ${pctColor(w.pct)} ${
                        i === 0 ? "font-bold" : ""
                      }`}
                      data-testid={`insight-${m.med_id}-w${i}`}
                    >
                      {pctText(w.pct)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </AppFrame>
  );
}
